import { Loader2, Plus } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserJobApplications } from '@/hooks/useUserJobApplications';
import { getInterviewUrl } from '@/lib/interview-url';

import DashboardLayout from '../../common/components/DashboardLayout';
import ConfirmDeleteCVDialog from '../components/ConfirmDeleteCVDialog';
import CVListEmpty from '../components/CVListEmpty';
import CVListEmptySearch from '../components/CVListEmptySearch';
import CVListError from '../components/CVListError';
import CVMiniature from '../components/CVMiniature';
import { CVListProvider } from '../contexts/CVListContext';
import { useCVList } from '../hooks/useCVList';
import { useDeleteCV } from '../hooks/useDeleteCV';
import { useDownloadCV } from '../hooks/useDownloadCV';

enum UIState {
  Loading = 'loading',
  Error = 'error',
  EmptySearch = 'emptySearch',
  Empty = 'empty',
  Ready = 'ready',
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { filteredCVs, isLoading, error, searchTerm, setSearchTerm, refetch } = useCVList();
  const { applications, error: applicationsError } = useUserJobApplications();
  const { downloadCVFromList, isDownloading, downloadingId } = useDownloadCV();
  const { deleteCVById, deletingId } = useDeleteCV();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleCreateCV = () => {
    const interviewUrl = getInterviewUrl();
    const url = new URL(interviewUrl);
    url.searchParams.set('source', 'Dashboard');
    window.location.href = url.toString();
  };

  const handleConfirmDelete = async (cvId: string) => {
    await deleteCVById(cvId, async () => {
      await refetch();
      setConfirmDeleteId(null);
    });
  };

  const handleDeleteCV = (cvId: string) => {
    setConfirmDeleteId(cvId);
  };

  const handleEditCV = (cvId: string) => {
    const interviewUrl = getInterviewUrl();
    window.location.href = new URL(`/cv/${cvId}/edit`, interviewUrl).toString();
  };

  const resolveUIState = (): UIState => {
    if (isLoading) return UIState.Loading;
    if (error) return UIState.Error;
    if (filteredCVs.length === 0 && searchTerm) return UIState.EmptySearch;
    if (filteredCVs.length === 0) return UIState.Empty;
    return UIState.Ready;
  };

  const stateViews: Record<UIState, ReactNode> = {
    [UIState.Loading]: (
      <div className="text-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t('dashboard.cvList.loading')}</p>
      </div>
    ),
    [UIState.Error]: (
      <CVListError
        title={t('dashboard.cvList.error.title')}
        description={t('dashboard.cvList.error.description')}
      />
    ),
    [UIState.EmptySearch]: (
      <CVListEmptySearch
        title={t('dashboard.cvList.noResults.title')}
        description={t('dashboard.cvList.noResults.description')}
      />
    ),
    [UIState.Empty]: (
      <CVListEmpty
        title={t('dashboard.cvList.empty.title')}
        description={t('dashboard.cvList.empty.description')}
        ctaLabel={t('dashboard.cvList.empty.createButton')}
        onCreate={handleCreateCV}
      />
    ),
    [UIState.Ready]: (
      <CVListProvider
        value={{
          onEdit: handleEditCV,
          onDownload: downloadCVFromList,
          onDelete: handleDeleteCV,
          deletingId,
          downloadingId,
          isDownloading,
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredCVs.map(cv => (
            <CVMiniature key={cv.id} cv={cv} />
          ))}
          <button
            type="button"
            onClick={handleCreateCV}
            className="pro-card p-4 flex flex-col items-center justify-center gap-2 border-dashed border-2 border-cj-green/20 hover:border-cj-green min-h-[100px] transition"
          >
            <div className="w-10 h-10 bg-cj-green-light/40 rounded-xl flex items-center justify-center">
              <Plus className="w-5 h-5 text-cj-green" />
            </div>
            <p className="text-xs font-semibold text-cj-green-dark">
              {t('dashboard.cvList.createButton')}
            </p>
          </button>
        </div>
      </CVListProvider>
    ),
  };

  const uiState = resolveUIState();

  return (
    <DashboardLayout
      title={t('dashboard.cvList.title')}
      subtitle={t('dashboard.cvList.subtitle')}
      showSearch={true}
      searchPlaceholder={t('dashboard.cvList.searchPlaceholder')}
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      onCreateAction={handleCreateCV}
      createButtonText={t('dashboard.cvList.createButton')}
    >
      {/* Stats row*/}
      <div className="grid grid-cols-2 gap-3 lg:gap-4 mb-6 max-w-3xl">
        <div className="bg-gradient-to-br from-cj-green to-cj-green-nuance rounded-2xl p-3.5 lg:p-4 text-white shadow-md shadow-cj-green/20">
          <p className="font-display text-2xl lg:text-3xl">
            {isLoading ? '–' : String(filteredCVs.length).padStart(2, '0')}
          </p>
          <p className="text-xs opacity-80 mt-1 font-medium">
            {t('dashboard.applications.cvsActive')}
          </p>
        </div>
        <div className="pro-card no-hover rounded-2xl p-3.5 lg:p-4">
          <p className="font-display text-2xl lg:text-3xl text-cj-green-dark">
            {applicationsError ? '–' : String(applications.length).padStart(2, '0')}
          </p>
          <p className="text-xs text-cj-green-dark/50 mt-1 font-medium">
            {t('dashboard.applications.label')}
          </p>
        </div>
      </div>

      {stateViews[uiState]}

      <ConfirmDeleteCVDialog
        cvId={confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={deletingId === confirmDeleteId}
      />
    </DashboardLayout>
  );
}
